package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.borderTop
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.trf
import kotlin.random.Random
import kotlinx.browser.document
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

data class LPageState(
    val shown: Int,
    /**
     * The server's page size, or **0 to say this count cannot be positioned**.
     *
     * A range — "Events 51 to 53" — is a claim about where these rows sit in the whole sequence, and
     * it is only true when every row the server sent is on screen. A caller that filters after the
     * fetch has no such claim to make, and passing 0 is how it says so: [paginationSentence] then
     * falls back to a bare count, on the first page as well as on later ones.
     */
    val pageSize: Int,
    val hasOlder: Boolean,
    val hasNewer: Boolean,
    val pageNumber: Int,
    val newerIsJumpToNewest: Boolean = false,
)

val LPaginationStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .alignItems(AlignItems.Center)
        .gap(Space.MD)
        .padding(topBottom = Space.SM, leftRight = Space.LG)
        .borderTop(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .styleModifier { property("display", "flex") }
}

@Composable
fun LPagination(
    state: LPageState,
    onOlder: () -> Unit,
    onNewer: () -> Unit,
    modifier: Modifier = Modifier,
    label: String = t("ds.ds.lpagination.pagination", "Pagination"),
    unit: String = t("ds.ds.lpagination.rows", "rows"),
    loading: Boolean = false,
) {
    val olderId = remember { "l-pagination-older-${Random.nextInt(1_000_000)}" }
    val newerId = remember { "l-pagination-newer-${Random.nextInt(1_000_000)}" }
    val rootId = remember { "l-pagination-${Random.nextInt(1_000_000)}" }
    var previousOlder by remember { mutableStateOf(state.hasOlder) }
    var previousNewer by remember { mutableStateOf(state.hasNewer) }

    LaunchedEffect(state.hasOlder, state.hasNewer, loading) {
        val active = document.activeElement as? HTMLElement
        val edgeChanged = previousOlder != state.hasOlder || previousNewer != state.hasNewer
        val pressedId = when (active?.id) {
            olderId -> if (!state.hasOlder || loading) olderId else null
            newerId -> if (!state.hasNewer || loading) newerId else null
            else -> if (active === document.body && edgeChanged) rootId else null
        }
        if (pressedId != null) {
            val targetId = when (pressedId) {
                olderId -> newerId.takeIf { state.hasNewer && !loading }
                newerId -> olderId.takeIf { state.hasOlder && !loading }
                else -> newerId.takeIf { state.hasNewer && !loading }
                    ?: olderId.takeIf { state.hasOlder && !loading }
            } ?: rootId
            (document.getElementById(targetId) as? HTMLElement)?.focus()
        }
        previousOlder = state.hasOlder
        previousNewer = state.hasNewer
    }

    Div(
        LPaginationStyle.toModifier().then(modifier).toAttrs {
            attr("id", rootId)
            attr("tabindex", "-1")
            attr("role", "navigation")
            attr("aria-label", label)
        },
    ) {
        Span(
            Modifier.styleModifier { property("margin-inline-start", "auto") }
                .toAttrs { attr("aria-live", "polite") },
        ) {
            Text(paginationSentence(state, unit))
        }
        Row(Modifier.gap(Space.SM)) {
            LButton(
                label = t("ds.ds.lpagination.older", "Older"),
                onClick = onOlder,
                modifier = Modifier.attr("id", olderId),
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
                // `loading`, not `enabled = … && !loading`. `LButton` keeps these deliberately
                // apart: `enabled = false` applies the native `disabled` attribute and takes the
                // control out of the tab order, while `loading` marks it `aria-disabled`, blocks the
                // click and sets `aria-busy` — so the reader who pressed it is still standing on it
                // when the page arrives. Routing busy through `enabled` dropped focus to the
                // document on every page turn, and the manual `aria-busy` here was overwritten by
                // the one `LButton` sets anyway.
                enabled = state.hasOlder,
                loading = loading,
            )
            LButton(
                label = if (state.newerIsJumpToNewest) t("ds.ds.lpagination.newest", "Newest") else t("ds.ds.lpagination.newer", "Newer"),
                onClick = onNewer,
                modifier = Modifier.attr("id", newerId),
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
                enabled = state.hasNewer,
                loading = loading,
            )
        }
    }
}


/**
 * The sentence under a page of rows, in the reader's language.
 *
 * All five forms were assembled here as raw English around a `unit` that was already translated, so
 * a German reader was shown "Showing the newest 25 Zeilen." — the noun localised, the sentence that
 * carries it not. They are catalogue entries now, with the count and the noun as **named**
 * placeholders so a translation may put either anywhere its own grammar wants them.
 *
 * `tPlural` rather than `trf` wherever a count decides the sentence: English needs one form here
 * and Polish needs three, and the count is the thing this sentence exists to state. `{shown}`
 * rather than `tPlural`'s own `{count}`, because the number a reader sees is grouped
 * (`formatCount`) and `{count}` is substituted from the raw `Int`; the raw count still selects the
 * category, which is what it is for.
 *
 * The last form used to be `"${unit.replaceFirstChar(Char::uppercase)} 1 to 25."` — a sentence
 * capitalised by moving the first character of a translated noun into upper case. That is wrong in
 * German (where the noun is already capitalised and the article is not), wrong in Turkish (whose
 * dotless `i` this does not know about), and impossible to express as a catalogue entry at all. It
 * now begins with a word this file owns, so nothing has to be re-cased.
 */
internal fun paginationSentence(state: LPageState, unit: String): String {
    if (state.shown <= 0) {
        return trf("ds.ds.lpagination.no_unit_on_this_page", "No {unit} on this page.", "unit" to unit)
    }
    // `pageSize == 0` is the caller saying "this count is filtered, and is not a window into a
    // sequence I can position". It has to be read *before* the first-page case, not after it: that
    // case claimed these were "the newest N", which is true of an unfiltered first page and false
    // the moment a client-side filter narrows one. The audit log filters its actor box after the
    // fetch, so 25 events narrowed to 3 announced "Showing the newest 3 events" — a completeness
    // claim about a governance page, which is the last place to make one loosely.
    if (state.pageSize <= 0) {
        return if (state.pageNumber <= 1) {
            tPlural(
                "ds.ds.lpagination.showing_shown_unit",
                state.shown,
                "Showing {shown} {unit}.",
                "Showing {shown} {unit}.",
                "shown" to formatCount(state.shown),
                "unit" to unit,
            )
        } else {
            tPlural(
                "ds.ds.lpagination.showing_shown_unit_from_further_back",
                state.shown,
                "Showing {shown} {unit} from further back.",
                "Showing {shown} {unit} from further back.",
                "shown" to formatCount(state.shown),
                "unit" to unit,
            )
        }
    }
    if (state.pageNumber <= 1) {
        return tPlural(
            "ds.ds.lpagination.showing_the_newest_shown_unit",
            state.shown,
            "Showing the newest {shown} {unit}.",
            "Showing the newest {shown} {unit}.",
            "shown" to formatCount(state.shown),
            "unit" to unit,
        )
    }
    val first = (state.pageNumber - 1) * state.pageSize + 1
    return trf(
        "ds.ds.lpagination.showing_unit_first_to_last",
        "Showing {unit} {first} to {last}.",
        "unit" to unit,
        "first" to formatCount(first),
        "last" to formatCount(first + state.shown - 1),
    )
}
